import React, { useState, useEffect } from 'react';

interface CountdownTimerProps {
  targetDate: Date | string;
  onComplete?: () => void;
  className?: string;
  variant?: 'default' | 'hero'; // 'hero' for hero section with white text
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  total: number;
}

const CountdownTimer: React.FC<CountdownTimerProps> = ({
  targetDate,
  onComplete,
  className = '',
  variant = 'default',
}) => {
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(null);

  useEffect(() => {
    const calculateTimeRemaining = (): TimeRemaining | null => {
      const target = new Date(targetDate);
      const now = new Date();
      const difference = target.getTime() - now.getTime();

      if (difference <= 0) {
        if (onComplete) {
          onComplete();
        }
        return null;
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000),
        total: difference,
      };
    };

    // Calculate immediately
    setTimeRemaining(calculateTimeRemaining());

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      if (!remaining) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, onComplete]);

  const isHero = variant === 'hero';

  if (!timeRemaining) {
    return (
      <div className={`text-sm ${className}`}>
        <span className={`font-semibold ${isHero ? 'text-white bg-[var(--color-success-500)] bg-opacity-80 px-3 py-1 rounded' : 'text-[var(--color-success-600)]'}`}>
          Session has started
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className={`flex items-center gap-1 text-sm font-medium ${isHero ? 'text-white' : 'text-[var(--color-default-font)]'}`}>
        <span className={`text-xs ${isHero ? 'text-white opacity-75' : 'text-[var(--color-subtext-color)]'}`}>Starts in:</span>
        {timeRemaining.days > 0 && (
          <span className={`px-2 py-1 rounded font-bold animate-bounce ${isHero ? 'bg-white bg-opacity-20 backdrop-blur-sm text-white' : 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]'}`} style={{ animationDelay: '0s' }}>
            {timeRemaining.days}d
          </span>
        )}
        <span className={`px-2 py-1 rounded font-bold animate-bounce ${isHero ? 'bg-white bg-opacity-20 backdrop-blur-sm text-white' : 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]'}`} style={{ animationDelay: '0.1s' }}>
          {String(timeRemaining.hours).padStart(2, '0')}h
        </span>
        <span className={`px-2 py-1 rounded font-bold animate-bounce ${isHero ? 'bg-white bg-opacity-20 backdrop-blur-sm text-white' : 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]'}`} style={{ animationDelay: '0.2s' }}>
          {String(timeRemaining.minutes).padStart(2, '0')}m
        </span>
        <span className={`px-2 py-1 rounded font-bold animate-bounce ${isHero ? 'bg-white bg-opacity-20 backdrop-blur-sm text-white' : 'bg-[var(--color-brand-100)] text-[var(--color-brand-700)]'}`} style={{ animationDelay: '0.3s' }}>
          {String(timeRemaining.seconds).padStart(2, '0')}s
        </span>
      </div>
    </div>
  );
};

export default CountdownTimer;

